import axios from 'axios';
import CryptoJS from 'crypto-js';
import { toast } from "../components/ThreatModelling/ui/use-toast";
import { LoginCredentials, LoginResult, OrganizationTrials, Project, User } from '../types/auth';
import { SBOMScan, SASTScanDetails, DASTScanDetails, INFRAScanDetails } from '../types/scan';
import { SBOMScanDetail, SBOMScanHistoryResponse } from '../types/sbom';
import { SecretScanDetail } from '../types/secrets';
import { SCAScanDetail } from '../types/sca';
import { ThreatModel } from '../types/threat-model';
import { NotificationResponse } from 'types/notifications';
import { getUserData, setEncryptedInStorage, setOrganizationTrials } from 'utils/encryption';
import { log } from 'utils/logger';
import * as logger from 'utils/logger';
import { getAccessToken, setAccessToken } from './tokenService';
import { joinUrl } from 'lib/utils';

const SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY || '';

// Prepare key exactly as backend does
const getKey = () => {
  // Take first 32 chars and pad with zeros
  const keyStr = SECRET_KEY.slice(0, 32).padEnd(32, '0');
  // Convert to UTF-8 bytes
  return CryptoJS.enc.Utf8.parse(keyStr);
};

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
// Promise for the refresh token request, which resolves with the new access token
let refreshPromise: Promise<string> | null = null;

// Add a request interceptor to pause requests during token refresh.
axios.interceptors.request.use(
  (config) => {
    // If a token refresh is in progress, and the request is not for the refresh token endpoint itself,
    // chain this request to the existing refresh promise.
    if (isRefreshing && !config.url?.includes('refresh')) {
      return refreshPromise!.then(token => {
        // Once the new token is available, update the request header and proceed.
        config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
    }
    return config;
  },
  (error) => {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Set up visibility change listener to check auth status when tab becomes active
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    log('Tab became active, checking auth status...');
    checkAuthStatus().catch(err => {
      logger.error('Error checking auth status on tab activation:', err);
    });
  }
});

// Add axios response interceptor for global error handling
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Handle 401 errors for token refresh
    if (
      status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('login') &&
      !originalRequest.url?.includes('refresh-token')
    ) {
      if (isRefreshing) {
        // A refresh is already in progress, chain onto the existing promise to retry the request.
        return refreshPromise!.then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      log('Attempting token refresh...');

      // Start the refresh token request and store the promise.
      refreshPromise = new Promise(async (resolve, reject) => {
        try {
          log('Interceptor: Sending refresh token request...');
          const response = await axios.post<LoginResult>(
            joinUrl(MONITOR_API, '/v2/auth/refresh-token'),
            {},
            {
              withCredentials: true, // Send cookies with the request
            }
          );
          log('Interceptor: Refresh token request successful.');

          const { access_token } = response.data as any;
          log('Token refresh successful');

          setAccessToken(access_token);
          
          // When the refresh is successful, resolve the promise with the new token.
          resolve(access_token);
        } catch (refreshError: any) {
          logger.error('Token refresh failed:', refreshError);
          if (refreshError.response?.status === 401) {
            setAccessToken(null);
            toast({
              variant: "destructive",
              title: "Session Expired",
              description: "Please log in again to continue accessing your account."
            });
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('auth-error'));
            }, 2000);
          }
          reject(refreshError);
        } finally {
          // Reset the state regardless of success or failure.
          isRefreshing = false;
          refreshPromise = null;
        }
      });

      // Chain the original request to the refresh promise.
      return refreshPromise.then(token => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return axios(originalRequest);
      });
    }

    // Handle other errors
    if (status === 500) {
      toast({
        variant: "destructive",
        title: "Server Error",
        description: "Something went wrong. Please try again later."
      });
    }

    return Promise.reject(error);
  }
);

export interface DirectoryStructure {
  name: string;
  children?: DirectoryStructure[];
}

export interface GlobalMetric {
  active_fixes: number;
  applications: number;
  findings: number;
  inactive_fixes: number;
  libraries: number;
  metric_id: number;
  organization: string;
  repos: number;
  scan_type: string;
  scans: number;
  severity_critical: number;
  severity_high: number;
  severity_low: number;
  severity_medium: number;
  threat_models_count: number;
  total_dread_score: number;
  total_recommendations: number;
  updated_at: string;
  user_id: number;
}

export interface DashboardMetrics {
  global_metrics: GlobalMetric[];
  total_projects: number;
}

const REACT_APP_BACKEND_URL = joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'api');
const REACT_APP_BACKEND_CORE_URL = joinUrl(process.env.REACT_APP_BACKEND_CORE_URL || '', 'api');

const MONITOR_API = process.env.REACT_APP_OAUTH_URL
  ? joinUrl(process.env.REACT_APP_OAUTH_URL, 'api')
  : joinUrl(process.env.REACT_APP_MONITOR_URL || 'https://monitor.openana.ai', 'api/v1/auth');


export interface ThreatModelScan {
  created_at: string;
  format: string;
  id: number;
  scan_id: number;
  project_id: number;
  report: {
    attack_tree: string;
    dread: string;
    mitigations: string;
    test_cases: string;
    threat_model: string;
  };
  summery?: ThreatModel;
  scan_type: string;
  status: string | null;
  updated_at: string;
  version: string;
}

export interface ThreatModelStats {
  scans: number;
  repos: number;
  total_recommendations: number;
  total_dread_score: number;
}

export interface SBOMStats {
  scans: number;
  repos: number;
  findings: number;
  libraries: number;
  applications: number;
  application_count?: number;
  file_count?: number;
  library_count?: number;
  total_findings?: number

}

export interface SCAStats {
  scans: number;
  repos: number;
  findings: number;
  severity_critical: number;
  severity_high: number;
  severity_medium: number;
  severity_low: number;
  severity_lowest: number;
  total_findings?: number;
}

export interface ScanStats {
  applications: number;
  findings: number;
  libraries: number;
  metric_id: number;
  repos: number;
  scan_type: string;
  scans: number;
  active_fixes: number;
  inactive_fixes: number;
  severity_critical: number;
  severity_high: number;
  severity_low: number;
  severity_medium: number;
  severity_info: number;
  threat_models_count: number;
  total_dread_score: number;
  total_recommendations: number;
  updated_at: string;
  user_id: number;
}

interface SolutionFile {
  id: number;
  commit_at: string | null;
  commit_desc: string | null;
  commit_message: string | null;
  created_at: string;
  file_content: string;
  filename: string;
  finding_id: string;
  fixed_content: string;
}

export interface Status {
  [step: string]: string;
}

export interface Extra {
  filepath: string
  finding_created_at: string
  finding_description: string
  finding_id: number
  finding_title: string
  project_id: number
  project_name: string
  project_organization: string
  project_repo_url: string
  scan_created_at: string
  scan_format: string
  scan_id: number
  scan_type: string
  scan_version: string
  severity: string;
  slug: string;
  metadata?: { // Added optional metadata property
    key?: string;
    self?: string;
    url?: string;
    [key: string]: any; // Allow other properties in metadata
  };
}

export interface AnsFindingSolution {
  finding_id: number;
  fixes: SolutionFile[];
  id: number;
  pr_url: string; // This is an empty array in your sample
  branch_name: string;
  status: Status;
  extra: Extra;
}

export interface SASTStats extends ScanStats { }
export interface DASTStats extends ScanStats { }
export interface InfraStats extends ScanStats { }

export const getDashboardMetrics = async (projectId?: number): Promise<DashboardMetrics> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication found');
    }

    const params: any = {};
    if (projectId) {
      params.project_id = projectId;
    }

    const response = await axios.get<DashboardMetrics>(joinUrl(REACT_APP_BACKEND_URL, 'dashboard'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch dashboard metrics.'
    });
    throw err;
  }
};

export const createProject = async (projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post<Project>(joinUrl(REACT_APP_BACKEND_URL, 'project'), projectData, {
      headers
    });
    toast({
      title: "Success",
      description: "Project created successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to create project.'
    });
    throw err;
  }
};

let fetchUserPromise: Promise<User | null> | null = null;

export const fetchUser = async (token?: string): Promise<User | null> => {
  const authToken = token || getAccessToken();
  if (!authToken) {
    return null;
  }

  if (!fetchUserPromise) {
    fetchUserPromise = new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get<User & { organization_trials?: any }>(
          joinUrl(MONITOR_API, '/v1/auth/me'),
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        resolve(response.data);
      } catch (error) {
        reject(error);
      } finally {
        fetchUserPromise = null; // Reset for next call
      }
    });
  }

  return fetchUserPromise;
};

export const checkAuthStatus = async (): Promise<User | null> => {
  try {
    const user = await fetchUser();

    // If response has no user ID, treat as session expired
    if (user?.user_id == null) {
      toast({
        title: 'Session Expired',
        description: 'Your session has expired. Please login again.',
        variant: 'destructive',
      });

      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 5000);

      return null;
    }

    return user;
  } catch (err : any) {
    // For 401 errors, try to refresh the token
    if (err.response?.status === 401) {
      // The axios interceptor will handle the token refresh.
      // We just need to reject the promise to let the interceptor do its job.
      return Promise.reject(err);
    }
    
    // For 403 errors
    if (err.response?.status === 403) {
      toast({
        variant: 'destructive',
        title: 'Authentication Error',
        description: 'Please login again.',
      });
      removeAuthToken();
      window.location.href = '/login';
      return null;
    }

    // For other errors, just show the error message but don't clear tokens
    toast({
      variant: 'destructive',
      title: 'Error',
      description: err.response?.data?.error || 'Failed to check authentication status.',
    });

    // Return null but don't clear tokens or redirect
    return null;
  }
};

export const checkOrgStatus = async (): Promise<OrganizationTrials | undefined> => {
  try {
    const headers = getAuthHeaders();
    const user = getUserData();

    if (!headers || !user) {
      logger.error("Missing headers or user data.");
      return;
    }

    const response = await axios.get<User & { organization_trials?: OrganizationTrials }>(
      joinUrl(REACT_APP_BACKEND_URL, `/me?role=${user.role}`),
      { headers }
    );

    const orgTrials = response.data.organization_trials;

    if (orgTrials) {
      setOrganizationTrials(orgTrials);
      return orgTrials;
    }

    logger.error("Organization trials not found in response.");
    return undefined;

  } catch (err: any) {
    logger.error("Error checking org status:", err?.message || err);
    return undefined;
  }
};



export const encryptData = (data: string): string => {
  try {
    // Generate random 16-byte IV
    const iv = CryptoJS.lib.WordArray.random(16);

    // Get properly formatted key
    const key = getKey();

    // Encrypt with AES-256-CBC
    const encrypted = CryptoJS.AES.encrypt(data, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    // Combine IV and ciphertext and encode as base64
    const combined = CryptoJS.lib.WordArray.create([
      ...iv.words,
      ...encrypted.ciphertext.words
    ]);

    return CryptoJS.enc.Base64.stringify(combined);
  } catch (error) {
    throw new Error('Failed to encrypt data');
  }
};

export const decryptData = (encryptedData: string): string => {
  try {
    // Decode base64
    const combined = CryptoJS.enc.Base64.parse(encryptedData);

    // Extract IV (first 16 bytes)
    const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));

    // Extract ciphertext (remaining bytes)
    const ciphertext = CryptoJS.lib.WordArray.create(combined.words.slice(4));

    // Get properly formatted key
    const key = getKey();

    // Create CipherParams object
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: ciphertext,
      key: key,
      iv: iv
    });

    // Decrypt
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    throw new Error('Failed to decrypt data');
  }
};

export const handleSSOLogin = async (): Promise<void> => {
  try {
    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'auth/saml'));
    window.location.href = response.data.redirectUrl;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'SSO login failed.'
    });
    throw err;
  }
};

export const loginUser = async (credentials: LoginCredentials): Promise<LoginResult> => {
  try {
    const encryptedPassword = encryptData(credentials.password);

    const params = new URLSearchParams();
    params.append('username', credentials.email);
    params.append('password', encryptedPassword);

    const response = await axios.post<{
      access_token: string;
      token_type: string;
      refresh_token: string;
    }>(joinUrl(MONITOR_API, '/v2/auth/login'), params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true, // 🔥 This is essential for cookies
    });


    return {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
      token_type: response.data.token_type
    };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Login failed. Please check your credentials.'
    });
    throw err;
  }
};

export const loginUserWithEncryptedPassword = async (credentials: { email: string; password: string }): Promise<LoginResult> => {
  try {
    // Password is already encrypted, don't encrypt again
    const params = new URLSearchParams();
    params.append('username', credentials.email);
    params.append('password', credentials.password); // Use password as-is (already encrypted)

    const response = await axios.post<{
      access_token: string;
      token_type: string;
      refresh_token: string;
    }>(joinUrl(MONITOR_API, '/v2/auth/login'), params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
     withCredentials: true,
    });

    return {
      accessToken: response.data.access_token,
      refreshToken: response.data.refresh_token,
      token_type: response.data.token_type
    };
  } catch (error: any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: error.response?.data?.error || 'Auto-login failed. Please check your credentials.'
    });
    throw error;
  }
};

export const logout = (): void => {
  const theme = localStorage.getItem('theme'); // preserve theme

  // Call the backend to clear the HttpOnly refresh token cookie
  axios.post(joinUrl(MONITOR_API, '/v2/auth/logout'), {}, { withCredentials: true });

  localStorage.clear(); // remove everything
  if (theme) {
    localStorage.setItem('theme', theme); // restore theme
  }

  setAccessToken(null);
  delete axios.defaults.headers.common['Authorization'];
};


export const getProjects = async (): Promise<Project[]> => {
  const headers = getAuthHeaders();
  if (!headers) {
    throw new Error('No authentication data found');
  }

  try {
    const response = await axios.get<Project[]>(joinUrl(REACT_APP_BACKEND_URL, 'project'), {
      headers, // ✅ Pass headers directly, not as a string
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch projects.'
    });
    throw err;
  }
};

export const getProjectHistory = async (projectId: number): Promise<any[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params: {
        scope: 'project',
        filter: 'history',
        project_id: projectId
      },
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch project history.'
    });
    throw err;
  }
};

export const getSBOMStats = async (id: number, scope?: string): Promise<SBOMStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let params: any;

    if (scope === "scan") {
      params = { scan_id: id, scope: "scan" };
    } else if (id === 0) {
      params = { project_id: id, scope: "global" };
    } else {
      params = { project_id: id, scope: "project" };
    }

    const response = await axios.get<SBOMStats>(joinUrl(REACT_APP_BACKEND_URL, 'sbom/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SBOM stats.'
    });
    throw err;
  }
};

export const getJiraStats = async (id: number, scope?: string): Promise<SCAStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let params: any;

    if (scope === "scan") {
      params = { scan_id: id, scope: "scan" };
    } else if (id === 0) {
      params = { project_id: id, scope: "global" };
    } else {
      params = { project_id: id, scope: "project" };
    }

    const response = await axios.get<SCAStats>(joinUrl(REACT_APP_BACKEND_URL, 'jira/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira stats.'
    });
    throw err;
  }
};

export const getAPIStats = async (projectId: number): Promise<SCAStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = { project_id: projectId, scope: "project" };

    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SCAStats>(joinUrl(REACT_APP_BACKEND_URL, 'api-security/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    // toast({
    //   variant: "destructive",
    //   title: "Error",
    //   description: error.response?.data?.error || 'Failed to fetch API Security stats.'
    // });
    throw err;
  }
};
export const getSCAStats = async (id: number, scope?: string): Promise<SCAStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let params: any;

    if (scope === "scan") {
      params = { scan_id: id, scope: "scan" };
    } else if (id === 0) {
      params = { project_id: id, scope: "global" };
    } else {
      params = { project_id: id, scope: "project" };
    }

    const response = await axios.get<SCAStats>(joinUrl(REACT_APP_BACKEND_URL, 'sca/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SCA stats.'
    });
    throw err;
  }
};

export const getSASTStats = async (id: number, scope?: string): Promise<SASTStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let params: any;

    if (scope === "scan") {
      params = { scan_id: id, scope: "scan" };
    } else if (id === 0) {
      params = { project_id: id, scope: "global" };
    } else {
      params = { project_id: id, scope: "project" };
    }

    const response = await axios.get<SASTStats>(joinUrl(REACT_APP_BACKEND_URL, 'sast/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SAST stats.'
    });
    throw err;
  }
};

export const getDASTStats = async (id: number, scope?: string): Promise<DASTStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let params: any;

    if (scope === "scan") {
      params = { scan_id: id, scope: "scan" };
    } else if (id === 0) {
      params = { project_id: id, scope: "global" };
    } else {
      params = { project_id: id, scope: "project" };
    }

    const response = await axios.get<DASTStats>(joinUrl(REACT_APP_BACKEND_URL, 'dast/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch DAST stats.'
    });
    throw err;
  }
};

export const getInfraStats = async (id: number, scope?: string): Promise<InfraStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let params: any;

    if (scope === "scan") {
      params = { scan_id: id, scope: "scan" };
    } else if (id === 0) {
      params = { project_id: id, scope: "global" };
    } else {
      params = { project_id: id, scope: "project" };
    }

    const response = await axios.get<InfraStats>(joinUrl(REACT_APP_BACKEND_URL, 'infra/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Infrastructure stats.'
    });
    throw err;
  }
};

export const getSecretsStats = async (id: number, scope?: string): Promise<SCAStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let params: any;

    if (scope === "scan") {
      params = { scan_id: id, scope: "scan" };
    } else if (id === 0) {
      params = { project_id: id, scope: "global" };
    } else {
      params = { project_id: id, scope: "project" };
    }

    const response = await axios.get<SCAStats>(joinUrl(REACT_APP_BACKEND_URL, 'secrets/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Secrets stats.'
    });
    throw err;
  }
};

export const getThreatModelStats = async (projectId?: number | null): Promise<ThreatModelStats> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = { project_id: projectId, scope: "project" };

    if (projectId === 0 || projectId === null) {
      params.scope = 'global';
    }

    const response = await axios.get<ThreatModelStats>(joinUrl(REACT_APP_BACKEND_URL, 'threat-model/stats'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch threat model stats.'
    });
    throw err;
  }
};

export const generateSBOMScan = async (projectId: number, first_name: string, last_name: string, excludedFolders?: string[]): Promise<{ id: number }> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'sbom'), {
      first_name: first_name,
      last_name: last_name,
      project_id: projectId,
      excluded_folders: excludedFolders
    }, {
      headers
    });
    toast({
      title: "Success",
      description: "SBOM scan initiated successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to generate SBOM scan.'
    });
    throw err;
  }
};

export const generateSCAScan = async (projectId: number, first_name: string, last_name: string, excludedFolders?: string[]): Promise<{ id: number }> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'sca'), {
      first_name: first_name,
      last_name: last_name,
      project_id: projectId,
      excluded_folders: excludedFolders
    }, {
      headers
    });
    toast({
      title: "Success",
      description: "SCA scan initiated successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to generate SCA scan.'
    });
    throw err;
  }
};

export const generateSecretsScan = async (projectId: number, first_name: string, last_name: string, excludedFolders?: string[]): Promise<{ id: number }> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'secrets'), {
      first_name: first_name,
      last_name: last_name,
      project_id: projectId,
      excluded_folders: excludedFolders
    }, {
      headers
    });
    toast({
      title: "Success",
      description: "Secrets scan initiated successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to generate secrets scan.'
    });
    throw err;
  }
};

export const generateThreatModelScan = async (projectId: number): Promise<{ id: number }> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'threat-model'), {
      project_id: projectId
    }, {
      headers
    });
    toast({
      title: "Success",
      description: "Threat model scan initiated successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to generate threat model scan.'
    });
    throw err;
  }
};

export const getSecretsScans = async (projectId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'secrets',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };
    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Secrets scans.'
    });
    throw err;
  }
};

export const getSCAScans = async (projectId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'sca',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };
    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SCA scans.'
    });
    throw err;
  }
};

export const getSASTScans = async (projectId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'sast',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };

    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }
    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SAST scans.'
    });
    throw err;
  }
};

export const getDASTScans = async (projectId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'dast',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };

    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }
    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch DAST scans.'
    });
    throw err;
  }
};

export const getInfraScans = async (projectId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'infra',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };


    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Infrastructure scans.'
    });
    throw err;
  }
};

export const getSBOMScans = async (
  projectId: number,
  limit?: number,
  offset?: number,
  search?: string,
  filter_by?: string
): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'sbom',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };

    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SBOM scans.'
    });
    throw err;
  }
};

export const getJiraScans = async (
  projectId: number,
  limit?: number,
  offset?: number,
  search?: string,
  filter_by?: string
): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'jira',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };

    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira scans.'
    });
    throw err;
  }
};

export const getSBOMScanDetail = async (scanId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanDetail | SecretScanDetail> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      id: scanId,
      limit: limit || 10,
      offset: offset || 0
    };

    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    const response = await axios.get(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });

    if (response.data.scan_type === 'secrets') {
      return response.data as SecretScanDetail;
    } else {
      return response.data as SBOMScanDetail;
    }
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch scan details.'
    });
    throw err;
  }
};

export const getAPIScans = async (projectId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      project_id: projectId,
      filter: 'api-security',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };


    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    if (projectId === 0) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || { scans: [], total: 0 };
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SBOM scans.'
    });
    throw err;
  }
};

export const getAPIScanDetail = async (scanId: number, limit?: number, offset?: number): Promise<SBOMScanDetail | SecretScanDetail> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      id: scanId,
      limit: limit || 10,
      offset: offset || 0
    };

    const response = await axios.get(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });

    if (response.data.scan_type === 'secrets') {
      return response.data as SecretScanDetail;
    } else {
      return response.data as SBOMScanDetail;
    }
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch scan details.'
    });
    throw err;
  }
};

export const getSCAScanDetail = async (scanId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SCAScanDetail> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }
    const params: any = {
      id: scanId,
      limit: limit || 10,
      offset: offset || 0
    }

    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    const response = await axios.get(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });

    if (response.data.scan_type !== 'sca') {
      throw new Error('Invalid scan type');
    }

    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SCA scan details.'
    });
    throw err;
  }
};

export const getJiraScanDetail = async (scanId: number, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SCAScanDetail> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }
    const params: any = {
      id: scanId,
      limit: limit || 10,
      offset: offset || 0
    }

    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    const response = await axios.get(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });

    if (response.data.scan_type !== 'jira') {
      throw new Error('Invalid scan type');
    }

    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira scan details.'
    });
    throw err;
  }
};

export const setAuthToken = (token: string | null): void => {
  setAccessToken(token);
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// This function is no longer needed as the refresh token is in an HttpOnly cookie
// export const setAuthRefreshToken = (token: string): void => {
//   if (!token) {
//     localStorage.removeItem('authRefreshToken');
//     return;
//   }
//
//   // Store refresh token
//   localStorage.setItem('authRefreshToken', token);
// };

// Add token validation helper
export const validateToken = async (token: string): Promise<boolean> => {
  if (!token) return false;

  try {
    // Test token with /me endpoint
    const user = await fetchUser(token);

    // Ensure we have a valid user response
    if (user && user.user_id) {
      // Set the token in axios defaults if valid
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return true;
    }
    return false;
  } catch (err : any) {
    logger.error('Token validation failed:', err);
    // Clear invalid token
    delete axios.defaults.headers.common['Authorization'];
    return false;
  }
};

export const getStoredToken = (): string | null => {
  return getAccessToken();
};
export const getAuthHeaders = (): Record<string, string> | null => {
  const token = getAccessToken();
  if (!token) {
    log("getAuthHeaders: Missing token");
    return null;
  }

  try {
    // Try to get user data from localStorage directly first
    const userDataRaw = localStorage.getItem('user');
    let user = null;
    
    if (userDataRaw) {
      // Always try to use the decryption function first
      try {
        user = getUserData();
      } catch (e) {
        // If decryption fails, try to parse it as JSON
        try {
          user = JSON.parse(userDataRaw);
        } catch (e : any) {
          logger.error('Error parsing user data from localStorage:', e);
        }
      }
    }
    
    // Simple headers - backend extracts trial data from JWT
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    // Add user/org headers if available
    if (user) {
      headers['x-user-id'] = user.user_id?.toString() || '';
      headers['x-org-id'] = user.org_names_list?.[0] || user.email || '';
    }

    return headers;
  } catch (err : any) {
    logger.error('Error in getAuthHeaders:', err);
    // Return basic auth header as fallback
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }
};
export const getCurrentUser = (): User | null => {
  const user = getUserData();
  if (!user) {
    return null;
  }

  try {
    return user;
  } catch (err : any) {
    logger.error('Error parsing user from localStorage:', err);
    return null;
  }
};


export const updateUser = async (payload: Partial<User>): Promise<User> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.put<User>(
      joinUrl(MONITOR_API, '/v1/auth/me'),
      payload,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    toast({
      title: "Success",
      description: "Profile updated successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.message || 'Failed to update profile.'
    });
    throw err;
  }
};

export const removeAuthToken = (): void => {
  setAccessToken(null);
  localStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
};

export const getThreatModels = async (projectId: number | null, limit?: number, offset?: number,
  search?: string,
  filter_by?: string): Promise<SBOMScanHistoryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }
    console.log(`project id ${projectId}`)

    const params: any = {
      project_id: projectId,
      filter: 'threat-model',
      scope: 'project',
      limit: limit || 10,
      offset: offset || 0
    };


    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    if (projectId === 0 || projectId === null) {
      params.scope = 'global';
    }

    const response = await axios.get<SBOMScanHistoryResponse>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data || {};
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch threat models.'
    });
    throw err;
  }
};

export const deleteProject = async (projectId: number): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    await axios.delete(joinUrl(REACT_APP_BACKEND_URL, 'project'), {
      params: { id: projectId },
      headers
    });
    toast({
      title: "Success",
      description: "Project deleted successfully!"
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to delete project.'
    });
    throw err;
  }
};

export const updateProject = async (projectId: number, projectData: Partial<Project>): Promise<Project> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.put<Project>(joinUrl(REACT_APP_BACKEND_URL, 'project'),
      projectData,
      {
        params: { id: projectId },
        headers
      }
    );
    toast({
      title: "Success",
      description: "Project updated successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to update project.'
    });
    throw err;
  }
};

export const bookmarkProject = async (projectId: number, starred: boolean): Promise<Project> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.put<Project>(
      joinUrl(REACT_APP_BACKEND_URL, 'project'),
      { starred: starred ? 1 : 0 },
      {
        params: { id: projectId },
        headers
      }
    );
    toast({
      title: "Success",
      description: starred ? "Project bookmarked successfully!" : "Project bookmark removed successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to update project bookmark status.'
    });
    throw err;
  }
};

export const getSASTScanDetails = async (
  scanId: number,
  limit?: number,
  offset?: number,
  filter_by?: string,
  search?: string
): Promise<SASTScanDetails> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }
    const params: any = {
      id: scanId,
      limit: limit || 10,
      offset: offset || 0,
    }
    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    const response = await axios.get<SASTScanDetails>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch SAST scan details.'
    });
    throw err;
  }
};

export const getInfraScanDetails = async (scanId: number, limit?: number, offset?: number, filter_by?: string,
  search?: string): Promise<INFRAScanDetails> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      id: scanId,
      limit: limit || 10,
      offset: offset || 0,
    }
    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }


    const response = await axios.get<INFRAScanDetails>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Infrastructure scan details.'
    });
    throw err;
  }
};

export const getProjectDirectories = async (projectId: number): Promise<DirectoryStructure[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<{ data: DirectoryStructure[] }>(joinUrl(REACT_APP_BACKEND_URL, 'project/get_dirs'), {
      params: { id: projectId },
      headers
    });

    return response.data.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch project directories.'
    });
    throw err;
  }
};

export const getDASTScanDetails = async (scanId: number, limit?: number, offset?: number, filter_by?: string,
  search?: string): Promise<DASTScanDetails> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const params: any = {
      id: scanId,
      limit: limit || 10,
      offset: offset || 0,
    }
    if (search) {
      params.search = search;
    }

    if (filter_by) {
      params.filter_by = filter_by;
    }

    const response = await axios.get<DASTScanDetails>(joinUrl(REACT_APP_BACKEND_URL, 'scans'), {
      params,
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch DAST scan details.'
    });
    throw err;
  }
};

interface FileContentResponse {
  content: string;
  filepath: string;
}

export const disconnectGitHub = async (): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'github/uninstall'), {
      headers
    });

    const data = response.data;

    toast({
      title: "Success",
      description: data.message || "GitHub app installation"
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || err.message || 'Failed to disconnect from GitHub.'
    });
    throw err;
  }
};


export const getFileContent = async (projectId: number, filepath: string, scan_type?: string): Promise<FileContentResponse[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'project/filecontent'), {
      project_id: projectId,
      filepath: filepath,
      scan_type: scan_type
    }, {
      headers
    });
    return response.data || [];
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch file content.'
    });
    throw err;
  }
};

export const saveScanSolution = async (payload: any): Promise<any> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'scans/solution'), payload, {
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to save solution.'
    });
    throw err;
  }
};

export const getSolution = async (
  findingId: number,
  showError: boolean = true
): Promise<AnsFindingSolution> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(REACT_APP_BACKEND_URL, 'scans/solution'), {
      params: { finding_id: findingId },
      headers
    });
    return response.data;
  } catch (err : any) {
    if (showError) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.response?.data?.error || 'Failed to fetch solution.'
      });
    }
    throw err;
  }
};


export const solveWithAna = async (payload: any, scanType: 'SAST' | 'sca' | 'sbom' | 'secrets' | 'DAST' | 'INFRA'): Promise<any> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(`${REACT_APP_BACKEND_CORE_URL}/${scanType.toLowerCase()}`, payload, {
      headers
    });
    // toast({
    //   title: "Success",
    //   description: "Ana is analyzing the vulnerability..."
    // });
    // log(response.data)
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to process with Ana.'
    });
    throw err;
  }
};

export const solveWithAnaV2 = async (limit?: number, offset?: number, payload?: any): Promise<AnsFindingSolution> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, `/ana/solution?limit=${limit}&offset=${offset}`), payload, {
      headers
    });
    // toast({
    //   title: "Success",
    //   description: "Ana is analyzing the vulnerability..."
    // });
    // log(response.data)
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to process with Ana.'
    });
    throw err;
  }
};

export const getRepoBranches = async (projectId: number): Promise<string[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<{ branches: string[] }>(joinUrl(REACT_APP_BACKEND_URL, 'repo_branches'), {
      params: { id: projectId },
      headers
    });

    return response.data.branches;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch repository branches.'
    });
    throw err;
  }
};

export const implementFix = async (payload: {
  finding_id: number;
  target_branch: string;
  action_type: 'commit' | 'pull_request';
}): Promise<any> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(joinUrl(REACT_APP_BACKEND_URL, 'implement_fix'), payload, {
      headers
    });
    toast({
      title: "Success",
      description: response?.data?.message || "Changes proposed successfully!"
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.message || 'Failed to implement fix.'
    });
    throw err;
  }
};

export const checkScanStatus = async (scanId: string | number, scanType: string): Promise<any> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No authentication token found"
      });
      throw new Error('No authentication token found');
    }

    log(`Checking status for ${scanType} scan ${scanId}`); // Debug log

    const response = await axios.get(joinUrl(REACT_APP_BACKEND_URL, scanType.toLowerCase()), {
      params: { scan_id: scanId },
      headers
    });

    log(`Status response:`, response.data); // Debug log

    // Handle all possible status types
    const normalizedResponse = {
      ...response.data,
      status: response.data.status
    };

    if (normalizedResponse.status === 'completed') {
      toast({
        title: "Success",
        description: "Scan completed successfully!"
      });
    } else if (normalizedResponse.status === 'failed') {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Scan failed. Please check the details."
      });
    }

    return normalizedResponse;
  } catch (err : any) {
    logger.error('Error checking scan status:', err); // Debug log
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to check scan status.'
    });
    throw err;
  }
};


export const disconnectCheckmarx = async (): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/uninstall'), {
      headers
    });

    const data = response.data;

    toast({
      title: "Success",
      description: data.message || "Checkmarx disconnected successfully"
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || err.message || 'Failed to disconnect from Checkmarx.'
    });
    throw err;
  }
};

export const disconnectBitbucket = async (): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'bitbucket/uninstall'), {
      headers
    });

    const data = response.data;

    toast({
      title: "Success",
      description: data.message || "Bitbucket app uninstalled successfully"
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || err.message || 'Failed to disconnect from Bitbucket.'
    });
    throw err;
  }
};

export interface WebhookResponse {
  status: string;
  webhook_url: string;
}

export interface ProjectResponse {
  id: number;
  name: string;
  description: string;
  repo_url: string | null;
  organization: string
  created_at: string
  updated_at: string
  invicti: number;
  // other project fields...
}

export const getProjectDetails = async (projectId: number): Promise<ProjectResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<ProjectResponse>(joinUrl(REACT_APP_BACKEND_URL, 'project'), {
      params: { id: projectId },
      headers
    });

    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch project details.'
    });
    throw err;
  }
};

export const getInvictiWebhookUrl = async (projectId: number): Promise<WebhookResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<WebhookResponse>(joinUrl(REACT_APP_BACKEND_URL, `invicti/${projectId}/webhook`), {
      headers
    });

    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch webhook URL.'
    });
    throw err;
  }
};

export interface CheckmarxConfigRequest {
  region: string;
  tenant: string;
  api_key: string;
}

export interface CheckmarxConfigResponse {
  api_key: string;
  created_at: string;
  id: number;
  region: string;
  tenant: string;
  status: number;
  updated_at: string;
  user_id: number;
}

export interface CheckmarxAuthStatus {
  status: number;
  message: string;
}

export interface CheckmarxProject {
  id: string;
  name: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  groups: string[];
  tags: Record<string, string>;
  repoUrl: string;
  mainBranch: string;
  origin: string;
  criticality: number;
  scmRepoId: string;
  imported_proj_name: string;
}

export const getCheckmarxProjects = async (): Promise<CheckmarxProject[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<{ projects: CheckmarxProject[] }>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/projects'), {
      headers
    });
    return response.data.projects;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Checkmarx projects'
    });
    throw err;
  }
};

export const getCheckmarxConfig = async (): Promise<CheckmarxConfigResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication found');
    }

    const response = await axios.get<CheckmarxConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/config'), {
      headers
    });
    return response.data;
  } catch (err : any) {
    if (err.response?.status === 404) {
      throw new Error('Config not found');
    }
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Checkmarx configuration'
    });
    throw err;
  }
};

export const checkCheckmarxAuth = async (): Promise<CheckmarxAuthStatus> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication found');
    }

    const config = await getCheckmarxConfig();
    if (!config) {
      return {
        status: 0,
        message: 'Checkmarx configuration not found'
      };
    }

    const response = await axios.get<CheckmarxAuthStatus>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/check-auth'), {
      headers
    });

    log("here")
    return {
      status: response.data.message === 'Authenticated' ? 1 : 0,
      message: response.data.message || 'Authentication check completed'
    };
  } catch (err : any) {
    log("here3")
    if (err.message === 'Config not found') {
      return {
        status: 0,
        message: 'Checkmarx configuration not found'
      };
    }
    // Return a failed auth status instead of throwing
    return {
      status: 0,
      message: err.response?.data?.error || 'Failed to check Checkmarx auth status'
    };
  }
};




export const checkJiraAuth = async (): Promise<JiraAuthStatus> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication found');
    }

    const response = await axios.get<JiraAuthStatus>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/check-auth'), {
      headers
    });

    return {
      status: response.data.message === 'Authenticated' ? 1 : 0,
      message: response.data.message || 'Authentication check completed'
    };
  } catch (err: any) {
    return {
      status: 0,
      message: err.response?.data?.error || 'Failed to check Jira auth status'
    };
  }
};

export const getJiraConfig = async (): Promise<JiraConfigResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication found');
    }

    const response = await axios.get<JiraConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/config'), {
      headers
    });
    return response.data;
  } catch (err : any) {
    if (err.response?.status === 404) {
      throw new Error('Config not found');
    }
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira configuration'
    });
    throw err;
  }
};

export const saveJiraConfig = async (config: JiraConfigRequest): Promise<JiraConfigResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let response;
    try {
      await getJiraConfig();
      response = await axios.put<JiraConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/config'), config, {
        headers
      });
    } catch (err : any) {
      if (err.message === 'Config not found') {
        response = await axios.post<JiraConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/config'), config, {
          headers
        });
      } else {
        throw err;
      }
    }

    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to save Jira configuration'
    });
    throw err;
  }
};

export const getJiraForgeInstallUrl = async (): Promise<string> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<JiraForgeInstallUrlResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/forge/install-url'), {
      headers
    });
    return response.data.install_url;
  } catch (err: any) {
    if (err.response?.status === 503) {
      throw new Error('Jira Forge installation URL is not configured. Please contact your administrator.');
    }
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira Forge installation URL'
    });
    throw err;
  }
};

export const getJiraForgeSites = async (): Promise<JiraForgeSite[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<JiraForgeSitesResponse | JiraForgeSite[]>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/forge/sites'), {
      headers
    });
    const data = response.data;
    return Array.isArray(data) ? data : data.sites ?? [];
  } catch (err: any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira sites'
    });
    throw err;
  }
};

export const registerJiraForgeSites = async (siteUrls: string[]): Promise<JiraForgeSitesResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const payload: JiraForgeSitesRequest = siteUrls.length === 1
      ? { site_url: siteUrls[0] }
      : { site_urls: siteUrls };

    const response = await axios.post<JiraForgeSitesResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/forge/sites'), payload, {
      headers
    });
    return response.data;
  } catch (err: any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to register Jira site URL(s)'
    });
    throw err;
  }
};

export const deleteJiraForgeSite = async (siteUrl: string): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    await axios.delete(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/forge/sites'), {
      headers,
      data: { site_url: siteUrl }
    });
  } catch (err: any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to delete Jira site'
    });
    throw err;
  }
};

export const saveCheckmarxConfig = async (config: CheckmarxConfigRequest): Promise<CheckmarxConfigResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let response;
    try {
      // Try to get existing config first
      await getCheckmarxConfig();
      // If config exists, update it
      response = await axios.put<CheckmarxConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/config'), config, {
        headers
      });
    } catch (err : any) {
      if (err.message === 'Config not found') {
        // If config doesn't exist, create new
        response = await axios.post<CheckmarxConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/config'), config, {
          headers
        });
      } else {
        throw err;
      }
    }

    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to save Checkmarx configuration'
    });
    throw err;
  }
};

export interface CheckmarxProjectMapRequest {
  project_id: number;
  tool_project_id: string;
  tool_project_name: string;
}

export interface CheckmarxProjectMapping {
  project_id: number;
  tool_project_id: string;
  tool_project_name: string;
}

export const getCheckmarxProjectMappings = async (): Promise<CheckmarxProjectMapping[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<CheckmarxProjectMapping[]>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/project_map'), {
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Checkmarx project mappings'
    });
    throw err;
  }
};

export const mapCheckmarxProject = async (data: CheckmarxProjectMapRequest): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    await axios.post(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/project_map'), data, {
      headers
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to map Checkmarx project'
    });
    throw err;
  }
};

export const refreshCheckmarx = async (): Promise<{ status: string; message: string }> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'checkmarx/refresh'), {
      headers
    });

    // Handle different response statuses
    switch (response.status) {
      case 200:
        toast({
          title: "Success",
          description: "Checkmarx scans refreshed successfully!"
        });
        return { status: 'completed', message: 'Refresh completed successfully' };

      case 202:
        toast({
          title: "Checkmarx",
          description: "Refresh started in background. This may take a few moments."
        });
        return { status: 'processing', message: response.data?.message || 'Refresh started in background' };

      default:
        throw new Error('Unexpected response status: ' + response.status);
    }
  } catch (err : any) {
    let errorMessage = 'Failed to refresh Checkmarx projects';

    if (err.response) {
      const statusCode = err.response.status;

      switch (statusCode) {
        case 400:
          errorMessage = err.response.data?.error || 'Invalid request. Please check your configuration.';
          break;
        case 500:
          errorMessage = err.response.data?.error || 'Server error. Please try again later.';
          break;
        default:
          errorMessage = err.response.data?.error || err.response.data?.message ||
            `Server error: ${statusCode}`;
      }
      logger.error('Error response:', err.response.data);
    } else if (err.request) {
      errorMessage = 'No response from server. Please check your connection.';
      logger.error('No response received:', err.request);
    } else {
      errorMessage = err.message || errorMessage;
      logger.error('Error:', err);
    }

    toast({
      variant: "destructive",
      title: "Error",
      description: errorMessage
    });

    return { status: 'failed', message: errorMessage };
  }
};

export interface WizConfigRequest {
  api_key: string;
  api_secret_key: string;
}

export interface WizConfigResponse {
  api_key: string;
  api_secret_key: string;
  created_at: string;
  id: number;
  status: number;
  updated_at: string;
  user_id: number;
}

export interface WizAuthStatus {
  status: number;
  message: string;
}

export const getWizConfig = async (): Promise<WizConfigResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication found');
    }

    const response = await axios.get<WizConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/config'), {
      headers
    });
    return response.data;
  } catch (err : any) {
    if (err.response?.status === 404) {
      throw new Error('Config not found');
    }
    // toast({
    //   variant: "destructive",
    //   title: "Error",
    //   description: error.response?.data?.error || 'Failed to fetch Wiz configuration'
    // });
    throw err;
  }
};

export const checkWizAuth = async (): Promise<WizAuthStatus> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication found');
    }

    const config = await getWizConfig();
    if (!config) {
      return {
        status: 0,
        message: 'Wiz configuration not found'
      };
    }

    const response = await axios.get<WizAuthStatus>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/check-auth'), {
      headers
    });

    return {
      status: response.data.message === 'Authenticated' ? 1 : 0,
      message: response.data.message || 'Authentication check completed'
    };
  } catch (err : any) {
    if (err.message === 'Config not found') {
      return {
        status: 0,
        message: 'Wiz configuration not found'
      };
    }
    return {
      status: 0,
      message: err.response?.data?.error || 'Failed to check Wiz auth status'
    };
  }
};

export const refreshWiz = async (): Promise<{ status: string; message: string }> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/refresh'), {
      headers
    });

    // Handle different response statuses
    switch (response.status) {
      case 200:
        toast({
          title: "Success",
          description: "Wiz scans refreshed successfully!"
        });
        return { status: 'completed', message: 'Refresh completed successfully' };

      case 202:
        toast({
          title: "Wiz",
          description: "Refresh started in background. This may take a few moments."
        });
        return { status: 'processing', message: response.data?.message || 'Refresh started in background' };

      default:
        throw new Error('Unexpected response status: ' + response.status);
    }
  } catch (err : any) {
    let errorMessage = 'Failed to refresh Wiz projects';

    if (err.response) {
      const statusCode = err.response.status;

      switch (statusCode) {
        case 400:
          errorMessage = err.response.data?.error || 'Invalid request. Please check your configuration.';
          break;
        case 500:
          errorMessage = err.response.data?.error || 'Server error. Please try again later.';
          break;
        default:
          errorMessage = err.response.data?.error || err.response.data?.message ||
            `Server error: ${statusCode}`;
      }
      logger.error('Error response:', err.response.data);
    } else if (err.request) {
      errorMessage = 'No response from server. Please check your connection.';
      logger.error('No response received:', err.request);
    } else {
      errorMessage = err.message || errorMessage;
      logger.error('Error:', err);
    }

    toast({
      variant: "destructive",
      title: "Error",
      description: errorMessage
    });

    return { status: 'failed', message: errorMessage };
  }
};

export const saveWizConfig = async (config: WizConfigRequest): Promise<WizConfigResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    let response;
    try {
      // Try to get existing config first
      await getWizConfig();
      // If config exists, update it
      response = await axios.put<WizConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/config'), config, {
        headers
      });
    } catch (err : any) {
      if (err.message === 'Config not found') {
        // If config doesn't exist, create new
        response = await axios.post<WizConfigResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/config'), config, {
          headers
        });
      } else {
        throw err;
      }
    }

    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to save Wiz configuration'
    });
    throw err;
  }
};

export interface WizProject {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface WizProjectMapping {
  project_id: number;
  tool_project_id: string;
  tool_project_name: string;
}

export const getWizProjects = async (): Promise<WizProject[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<{ projects: WizProject[] }>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/projects'), {
      headers
    });
    return response.data.projects;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Wiz projects'
    });
    throw err;
  }
};

export const getWizProjectMappings = async (): Promise<WizProjectMapping[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<WizProjectMapping[]>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/project_map'), {
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Wiz project mappings'
    });
    throw err;
  }
};

export const mapWizProject = async (data: { project_id: number; tool_project_id: string; tool_project_name: string }): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    await axios.post(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/project_map'), data, {
      headers
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to map Wiz project'
    });
    throw err;
  }
};


export const disconnectWiz = async (): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'wiz/uninstall'), {
      headers
    });

    const data = response.data;

    toast({
      title: "Success",
      description: data.message || "Wiz disconnected successfully"
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || err.message || 'Failed to disconnect from Wiz.'
    });
    throw err;
  }
};


export const disconnectJira = async (): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.delete(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/uninstall'), {
      headers
    });

    const data = response.data;

    toast({
      title: "Success",
      description: data.message || "Jira disconnected successfully"
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || err.message || 'Failed to disconnect from Jira.'
    });
    throw err;
  }
};

export const markNotificationAsRead = async (notificationId: number): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    await axios.patch(joinUrl(REACT_APP_BACKEND_URL, 'notification'), null, {
      params: { id: notificationId },
      headers
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to mark notification as read.'
    });
    throw err;
  }
};

export const getNotifications = async (limit: number = 10, offset: number = 0): Promise<NotificationResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    log('Fetching notifications...');
    const response = await axios.get<NotificationResponse>(
      joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'api/notification'),
      {
        params: {
          limit,
          offset
        },
        headers,
        timeout: 10000 // 10 second timeout
      }
    );

    log('Notifications fetched successfully:', response.data.notifications.length);

    // Save trial information to localStorage with encryption
    if (response.data?.organization_trials) {
      setEncryptedInStorage('organization_trials', response.data.organization_trials);
    }
    
    return response.data;
  } catch (err : any) {
    // toast({
    //   variant: "destructive",
    //   title: "Error",
    //   description: err.response?.data?.error || 'Failed to fetch notifications.'
    // });
    throw err;
  }
};

// Guided Tour API functions
export interface GuidedTourStatus {
  tour_dashboard_status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  tour_integration_status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  tour_scan_status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  tour_fix_status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  tour_askana_status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
}

export const getGuidedTourStatus = async (): Promise<GuidedTourStatus> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<GuidedTourStatus>(joinUrl(REACT_APP_BACKEND_URL, 'guided-tour'), {
      headers
    });

    return response.data;
  } catch (err : any) {
    logger.error('Error fetching guided tour status:', err);
    // Return default status if API call fails
    return {
      tour_dashboard_status: 'not_started',
      tour_integration_status: 'not_started',
      tour_scan_status: 'not_started',
      tour_fix_status: 'not_started',
      tour_askana_status: 'not_started'
    };
  }
};

export const updateGuidedTourStatus = async (
  statusField: string,
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped'
): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    await axios.put(joinUrl(REACT_APP_BACKEND_URL, 'guided-tour'), {
      [statusField]: status
    }, {
      headers
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to update guided tour status.'
    });
    logger.error('Error updating guided tour status:', err);
  }
};


// Chat API interfaces
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  content: string;
  finished?: boolean;
}

export interface ChatSession {
  id: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessageResponse {
  id: string;
  session_id: string;
  message: string;
  type: 'user' | 'assistant';
  file?: string | null;
  created_at: string;
}

// Upload file to chatbot
export const uploadChatbotFile = async (file: File, userId: string): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId || "user_123");

    const response = await axios.post(`${REACT_APP_BACKEND_CORE_URL}/chatbot/upload`, formData);

    return response.data.file_id;
  } catch (err : any) {
    logger.error('Error uploading file:', err);
    throw err;
  }
};

// Stream chat with Ana
export const streamChat = async (
  userId: string | null,
  message: string,
  history: ChatMessage[],
  fileIds: string[],
  onChunk: (chunk: string) => void
): Promise<void> => {
  try {
    log("user_id", userId);
    const authHeaders = getAuthHeaders();
    if (!authHeaders) {
      throw new Error('No authentication token found');
    }

    // Use fetch for better stream handling in the browser
    const response = await fetch(`${REACT_APP_BACKEND_CORE_URL}/chatbot/stream`, {
      method: 'POST',
      headers: authHeaders, // getAuthHeaders includes Content-Type
      body: JSON.stringify({
        user_id: userId,
        message: message,
        history: history,
        files: fileIds
      })
    });

    if (!response.ok || !response.body) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to read error response from stream.' }));
      const error: any = new Error(errorData.error || `Request failed with status ${response.status}`);
      error.response = { data: errorData };
      throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      
      // Keep the last, possibly incomplete, line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            if (!data.finished && data.content) {
              fullResponse += data.content;
              onChunk(fullResponse);
            }
          } catch (e: any) {
            logger.error('Error parsing SSE data:', line, e);
          }
        }
      }
    }
  } catch (err: any) {
    logger.error('Error in chat stream:', err);
    throw err;
  }
};



// Chat API functions
export const getChatMessages = async (sessionId: string): Promise<ChatMessageResponse[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<ChatMessageResponse[]>(joinUrl(REACT_APP_BACKEND_URL, 'chat/message'), {
      params: { session_id: sessionId },
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch chat messages.'
    });
    throw err;
  }
};

export const createChatMessage = async (
  sessionId: string,
  message: string,
  type: 'user' | 'assistant',
  file?: string | null
): Promise<ChatMessageResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post<ChatMessageResponse>(joinUrl(REACT_APP_BACKEND_URL, 'chat/message'), {
      session_id: sessionId,
      message,
      type,
      file
    }, { headers });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to create chat message.'
    });
    throw err;
  }
};


export type { SBOMScan, SolutionFile };

export interface JiraConfigRequest {
  base_url: string;
  email: string;
  api_token: string;
}

export interface JiraConfigResponse {
  id: number;
  base_url: string; // Changed from 'url' to 'base_url'
  username: string;
  api_key: string;
  user_id: number;
  created_at: string;
  updated_at: string;
}

export interface JiraAuthStatus {
  status: number;
  message: string;
}

export interface JiraForgeInstallUrlResponse {
  install_url: string;
}

export interface JiraForgeSitesRequest {
  site_url?: string;
  site_urls?: string[];
}

export interface JiraForgeSite {
  id?: number;
  site_url: string;
  status?: string;
  cloud_id?: string;
  forge_installed?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

export interface JiraForgeSitesResponse {
  sites?: JiraForgeSite[];
  message?: string;
}

export interface JiraProject {
  id: string;
  name: string;
}

export interface JiraProjectMapping {
  project_id: number;
  tool_project_id: string;
  tool_project_name: string;
  default_branch?: string;
}

export const getJiraProjects = async (siteUrl?: string): Promise<JiraProject[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<{ projects: JiraProject[] }>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/projects'), {
      headers,
      params: siteUrl ? { site_url: siteUrl } : undefined
    });
    return response.data.projects;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira projects'
    });
    throw err;
  }
};

export const getJiraProjectMappings = async (): Promise<JiraProjectMapping[]> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<JiraProjectMapping[]>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/project_map'), {
      headers
    });
    return response.data;
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to fetch Jira project mappings'
    });
    throw err;
  }
};

export const mapJiraProject = async (data: { project_id: number; tool_project_id: string; tool_project_name: string; default_branch?: string }): Promise<void> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    await axios.post(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/project_map'), data, {
      headers
    });
  } catch (err : any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to map Jira project'
    });
    throw err;
  }
};

export interface JiraIssueCreationPayload {
  project_id: number;
  summary: string;
  description: string;
  severity: string;
  scan_id: number;
  finding_id: number;
}

export interface JiraIssueCreationResponse {
  id: string;
  key: string;
  self: string;
}

export const createJiraIssue = async (payload: JiraIssueCreationPayload): Promise<JiraIssueCreationResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post<JiraIssueCreationResponse>(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/issue'), payload, {
      headers
    });
    toast({
      title: "Success",
      description: `Jira issue ${response.data.key} created successfully!`
    });
    return response.data;
  } catch (err: any) {
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.error || 'Failed to create Jira issue.'
    });
    throw err;
  }
};

export const refreshJira = async (): Promise<{ status: string; message: string }> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get(joinUrl(process.env.REACT_APP_BACKEND_URL || '', 'jira/refresh'), {
      headers
    });

    switch (response.status) {
      case 200:
        toast({
          title: "Success",
          description: "Jira issues refreshed successfully!"
        });
        return { status: 'completed', message: 'Refresh completed successfully' };

      case 202:
        toast({
          title: "Jira",
          description: "Refresh started in background. This may take a few moments."
        });
        return { status: 'processing', message: response.data?.message || 'Refresh started in background' };

      default:
        throw new Error('Unexpected response status: ' + response.status);
    }
  } catch (err : any) {
    let errorMessage = 'Failed to refresh Jira issues';
    if (err.response) {
      errorMessage = err.response.data?.error || err.response.data?.message || `Server error: ${err.response.status}`;
    } else if (err.request) {
      errorMessage = 'No response from server. Please check your connection.';
    } else {
      errorMessage = err.message || errorMessage;
    }

    toast({
      variant: "destructive",
      title: "Error",
      description: errorMessage
    });

    return { status: 'failed', message: errorMessage };
  }
};
